import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type { StaffUser } from "../types";
import { useToast } from "../context/ToastContext";
import { useDrawer } from "../context/DrawerContext";

const SOURCE_CHANNELS = [
  { value: "meta_ads", label: "Meta Ads" },
  { value: "instagram", label: "Instagram" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "referral", label: "Referido" },
  { value: "resale", label: "Reventa" },
  { value: "other", label: "Otro" },
];

export default function LeadForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { show } = useToast();
  const { open } = useDrawer();
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [services, setServices] = useState<{ id: string | number; code: string; name: string }[]>([]);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sourceChannel, setSourceChannel] = useState("meta_ads");
  const [sourceCampaign, setSourceCampaign] = useState("");
  const [serviceCode, setServiceCode] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [duplicate, setDuplicate] = useState<{ contact: any } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.users().then(setUsers).catch(() => {});
    api.getServicesLookup().then(setServices).catch(() => {});
  }, []);

  async function submit(e: any, force = false) {
    e?.preventDefault?.();
    if (!fullName.trim() || !phone.trim() || !serviceCode.trim()) {
      show("Completá nombre, WhatsApp y servicio.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.createLead({
        full_name: fullName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        source_channel: sourceChannel,
        source_campaign: sourceCampaign.trim() || undefined,
        service_code: serviceCode.trim(),
        owner_user_id: ownerId || undefined,
        force,
      });
      if (res.duplicate) {
        setDuplicate({ contact: res.contact });
      } else {
        show("Lead creado correctamente.", "success");
        onCreated();
        onClose();
      }
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo crear el lead.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e: any) => e.stopPropagation()}>
        <h3>Nuevo lead</h3>
        {duplicate ? (
          <div>
            <p>
              Ya existe un contacto con este teléfono: <b>{duplicate.contact.full_name}</b>.
            </p>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={onClose}>
                Cerrar
              </button>
              <button
                className="btn-ghost"
                onClick={() => {
                  open(duplicate.contact.id);
                  onClose();
                }}
              >
                Ver ficha 360°
              </button>
              <button className="btn-primary" onClick={(e: any) => submit(e, true)} disabled={submitting}>
                Crear de todas formas
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="form-grid">
            <label>Nombre completo</label>
            <input value={fullName} onChange={(e: any) => setFullName(e.target.value)} required />
            <label>WhatsApp</label>
            <input value={phone} onChange={(e: any) => setPhone(e.target.value)} placeholder="+54 9 11..." required />
            <label>Email (opcional)</label>
            <input type="email" value={email} onChange={(e: any) => setEmail(e.target.value)} />
            <label>Fuente</label>
            <select value={sourceChannel} onChange={(e: any) => setSourceChannel(e.target.value)}>
              {SOURCE_CHANNELS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <label>Campaña (opcional)</label>
            <input value={sourceCampaign} onChange={(e: any) => setSourceCampaign(e.target.value)} />
            <label>Servicio de interés</label>
            <select value={serviceCode} onChange={(e: any) => setServiceCode(e.target.value)} required>
              <option value="">Seleccionar servicio</option>
              {services.map((s) => (
                <option key={s.id} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
            <label>Responsable (opcional)</label>
            <select value={ownerId} onChange={(e: any) => setOwnerId(e.target.value)}>
              <option value="">Sin asignar (IA)</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
            <label>Primera próxima acción (referencia)</label>
            <input value={nextAction} onChange={(e: any) => setNextAction(e.target.value)} placeholder="ej: Responder con IA" />

            <div className="modal-actions">
              <button type="button" className="btn-ghost" onClick={onClose}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Creando..." : "Crear lead"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
